import authService from "../services/authService";
// User Service for managing user access and permissions
class UserService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
    this.apiKey = process.env.REACT_APP_API_KEY;
  }

  async authHeaders(extra = {}) {
    const idToken = await authService.getIdToken();
    return { ...extra, Authorization: `Bearer ${idToken}` };
  }

  // Check if user is approved and has access
  async checkUserAccess(userEmail) {
    if (!userEmail) {
      return {
        hasAccess: false,
        isApproved: false,
        message: 'Email is required'
      };
    }

    try {
      const response = await fetch(`${this.baseUrl}/checkUserAccess`, {
        method: 'POST',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ email: userEmail })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success && result.error) {
        throw new Error(result.error);
      }

      // Accept both response shapes from the API:
      // 1) { success: true, data: { hasAccess, isApproved, message?, user? } }
      // 2) { success: true, hasAccess, isApproved, message }
      if (result.data) {
        return result.data;
      }

      if (
        typeof result.hasAccess !== 'undefined' ||
        typeof result.isApproved !== 'undefined' ||
        result.message
      ) {
        return {
          hasAccess: Boolean(result.hasAccess),
          isApproved: Boolean(result.isApproved),
          message: result.message || '',
          ...(result.user ? { user: result.user } : {})
        };
      }

      // Fallback: user might not exist
      return {
        hasAccess: false,
        isApproved: false,
        message: 'User not found. Please contact administrator for access.'
      };
    } catch (error) {
      console.error('Error checking user access:', error);
      return {
        hasAccess: false,
        isApproved: false,
        message: 'Error checking access. Please try again later.'
      };
    }
  }

  // Get all users (admin only)
  async getAllUsers(requesterEmail) {
    try {
      const idToken = await authService.getIdToken();
      const response = await fetch(`${this.baseUrl}/getUsers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
          ...(requesterEmail ? { 'x-requester-email': requesterEmail } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.data && Array.isArray(result.data.users)) {
        return result.data.users;
      }
      
      if (Array.isArray(result.users)) {
        return result.users;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  }

  // Get pending users (admin only)
  async getPendingUsers(requesterEmail) {
    try {
      const response = await fetch(`${this.baseUrl}/getPendingUsers`, {
        method: 'GET',
        headers: await this.authHeaders({
          'Content-Type': 'application/json',
          ...(requesterEmail ? { 'x-requester-email': requesterEmail } : {})
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.data && Array.isArray(result.data.users)) {
        return result.data.users;
      }
      
      if (Array.isArray(result.users)) {
        return result.users;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching pending users:', error);
      return [];
    }
  }

  // Approve user (admin only)
  async approveUser(userId, approvedBy, accessLevel, allowedCountries, allowedSections) {
    try {
      const response = await fetch(`${this.baseUrl}/approveUser`, {
        method: 'POST',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId,
          approvedBy,
          accessLevel,
          allowedCountries,
          allowedSections
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Check if the response indicates success (either success: true or message indicating success)
      if (result.success === false || (result.error && !result.message?.includes('successfully'))) {
        throw new Error(result.error || 'Failed to approve user');
      }

      return result.data || result;
    } catch (error) {
      console.error('Error approving user:', error);
      throw error;
    }
  }

  // Reject user (admin only)
  async rejectUser(userId, rejectedBy, reason) {
    try {
      const response = await fetch(`${this.baseUrl}/rejectUser`, {
        method: 'POST',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId,
          rejectedBy,
          reason
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Check if the response indicates success (either success: true or message indicating success)
      if (result.success === false || (result.error && !result.message?.includes('successfully'))) {
        throw new Error(result.error || 'Failed to reject user');
      }

      return result.data || result;
    } catch (error) {
      console.error('Error rejecting user:', error);
      throw error;
    }
  }

  // Update user permissions (admin only)
  async updateUserPermissions(userId, accessLevel, allowedCountries, allowedSections, updatedBy, emailNotificationsEnabled) {
    try {
      const response = await fetch(`${this.baseUrl}/updateUserPermissions`, {
        method: 'PUT',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId,
          accessLevel,
          allowedCountries,
          allowedSections,
          updatedBy,
          ...(typeof emailNotificationsEnabled === 'boolean'
            ? { emailNotificationsEnabled }
            : {})
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Accept both shapes: { success: true, data } OR { message: '...successfully' }
      if (result?.success === false || (result?.error && !result?.message?.includes('successfully'))) {
        throw new Error(result.error || 'Failed to update user permissions');
      }

      return result.data || result;
    } catch (error) {
      console.error('Error updating user permissions:', error);
      throw error;
    }
  }

  // Update only email notification preference (admin only)
  async updateUserEmailNotifications(userId, updatedBy, emailNotificationsEnabled) {
    if (!userId || !updatedBy || typeof emailNotificationsEnabled !== 'boolean') {
      throw new Error('User ID, updater email, and emailNotificationsEnabled are required');
    }

    try {
      const response = await fetch(`${this.baseUrl}/updateUserPermissions`, {
        method: 'PUT',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId,
          updatedBy,
          emailNotificationsEnabled
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result?.success === false || (result?.error && !result?.message?.includes('successfully'))) {
        throw new Error(result.error || 'Failed to update email notification preference');
      }

      return result.data || result;
    } catch (error) {
      console.error('Error updating email notification preference:', error);
      throw error;
    }
  }

  // Deactivate user (admin only)
  async deactivateUser(userId, deactivatedBy, reason) {
    try {
      const response = await fetch(`${this.baseUrl}/deactivateUser`, {
        method: 'POST',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userId,
          deactivatedBy,
          reason
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to deactivate user');
      }

      return result.data;
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  }

  // Get user permissions
  async getUserPermissions(userEmail) {
    try {
      const response = await fetch(`${this.baseUrl}/users/permissions`, {
        method: 'POST',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ email: userEmail })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user permissions');
      }

      const result = await response.json();
      return result.data || { accessLevel: 'none', allowedCountries: [], allowedSections: [] };
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return { accessLevel: 'none', allowedCountries: [], allowedSections: [] };
    }
  }

  // Check if user can access specific section
  async canAccessSection(userEmail, section) {
    try {
      const permissions = await this.getUserPermissions(userEmail);
      return permissions.allowedSections.includes(section) || permissions.accessLevel === 'admin';
    } catch (error) {
      console.error('Error checking section access:', error);
      return false;
    }
  }

  // Check if user can access specific country
  async canAccessCountry(userEmail, country) {
    try {
      const permissions = await this.getUserPermissions(userEmail);
      return permissions.allowedCountries.includes(country) || permissions.accessLevel === 'admin';
    } catch (error) {
      console.error('Error checking country access:', error);
      return false;
    }
  }

  // Get user activity logs
  async getUserActivityLogs(userId, limit = 50) {
    try {
      const response = await fetch(`${this.baseUrl}/getUserActivityLogs?userId=${userId}&limit=${limit}`, {
        method: 'GET',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' })
      });

      const result = await response.json();
      if (result?.error) {
        throw new Error(result.error || 'Failed to fetch user activity logs');
      }
      if (Array.isArray(result?.logs)) return result.logs;
      if (result?.data && Array.isArray(result.data.logs)) return result.data.logs;
      return [];
    } catch (error) {
      console.error('Error fetching user activity logs:', error);
      return [];
    }
  }

  // Get activity logs with optional filters (admin/reporting use)
  async getActivityLogs({ action, userId, limit = 100, offset = 0 } = {}) {
    try {
      const params = new URLSearchParams();
      if (action) params.set('action', action);
      if (userId) params.set('userId', userId);
      params.set('limit', String(limit));
      params.set('offset', String(offset));

      const response = await fetch(`${this.baseUrl}/getUserActivityLogs?${params.toString()}`, {
        method: 'GET',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result?.error) {
        throw new Error(result.error || 'Failed to fetch activity logs');
      }

      const payload = result?.data || result || {};
      return {
        logs: Array.isArray(payload.logs) ? payload.logs : [],
        total: Number(payload.total || 0),
        limit: Number(payload.limit || limit),
        offset: Number(payload.offset || offset),
        hasMore: Boolean(payload.hasMore)
      };
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      return { logs: [], total: 0, limit, offset, hasMore: false };
    }
  }

  // Log user activity
  async logUserActivity(userEmail, action, details) {
    try {
      const response = await fetch(`${this.baseUrl}/logUserActivity`, {
        method: 'POST',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          userEmail,
          action,
          details,
          timestamp: new Date().toISOString()
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to log user activity');
      }

      return result.data;
    } catch (error) {
      console.error('Error logging user activity:', error);
    }
  }

  // Validate UNICEF domain
  isUnicefDomain(email) {
    return email && email.toLowerCase().endsWith('@unicef.org');
  }

  // Get access levels
  getAccessLevels() {
    return [
      { value: 'country', label: 'Country', description: 'Access to specific country data and assigned sections only' },
      { value: 'country-admin', label: 'Country Admin', description: 'Admin of a specific country can view/manage users for that country (approve/reject).' },
      { value: 'regional', label: 'Regional', description: 'Access to multiple countries within a specific region and assigned sections' },
      { value: 'global', label: 'Global', description: 'Access to all countries and sections' },
      { value: 'admin', label: 'Administrator', description: 'Full access to all features, user management, and system administration' }
    ];
  }

  // Get available sections
  getAvailableSections() {
    return [
      { value: 'stock-performance', label: 'Stock Performance / V2 Home', description: 'Monitor stock metrics; also grants /v2, /v2/new-vaccines, and /v2/demand-vaccine-trust access' },
      { value: 'co-instance', label: 'CO Instance', description: 'Country stock performance' },
      { value: 'data-warehouse', label: 'Data Warehouse', description: 'Central repository for analytics data' },
      { value: 'tracking', label: 'Tracking', description: 'Track and monitor operations progress' },
      { value: 'ticketing-system', label: 'Ticketing System', description: 'Log and manage support tickets' },
      { value: 'co-feedback', label: 'CO Feedback', description: 'Collect feedback from country offices' },
      { value: 'resolution-matrices', label: 'COE TA Tracker', description: 'Country office technical assistance tracker' },
      { value: 'inventories', label: 'Inventories', description: 'Available cold chain equipment inventory' },
      { value: 'ucc-inventories', label: 'UCC Inventories', description: 'Inventory of Ultra-Low-Temperature Freezers (ULT-F)' },
      { value: 'temperature-rtmd', label: 'Temperature RTMD', description: 'Real-time monitoring devices data' },
      { value: 'gaps', label: 'Gaps', description: 'Identify cold chain capacity gaps' },
      { value: 'scip', label: 'SCIP', description: 'Supply Chain Improvement Plan' },
      { value: 'fsp4all', label: 'FSP4All', description: 'Supply chain improvement planning' },
      { value: 'evma', label: 'EVMA', description: 'Effective vaccine management assessment' },
      { value: 'nlwg-maturity', label: 'NLWG Maturity', description: 'National logistics working group maturity' },
      { value: 'drive', label: 'DRIVE', description: 'Last mile delivery dashboard' },
      { value: 'fsp4all-forecasting', label: 'FSP4All Forecasting', description: 'Advanced demand forecasting and supply planning' },
      { value: 'solarization', label: 'Solarization', description: 'Use of solar-powered solutions' },
      { value: 'waste-management', label: 'Waste Management', description: 'Reduce, reuse and recycle waste' },
      { value: 'carbon-footprint', label: 'Carbon Footprint', description: 'Measure & offset CO2 emissions' },
      { value: 'coverage-data', label: 'Coverage Data', description: 'Immunization coverage statistics' },
      { value: 'new-vaccine-intro', label: 'New Vaccine Intro', description: 'Introducing new vaccine programmes' },
      { value: 'outbreaks', label: 'Outbreaks / V2 Outbreaks', description: 'Monitor and respond to outbreaks; also grants /v2/outbreaks access' },
      { value: 'budgets', label: 'Budgets / V2 Budgets', description: 'Plan and monitor immunization programme budgets; also grants /v2/budgets access' },
      { value: 'admin', label: 'Admin Panel', description: 'Administrative functions and user management' }
    ];
  }

  // Get available countries from geo_admin table
  async getAvailableCountries() {
    try {
      const response = await fetch(`${this.baseUrl}/getCountries`, {
        method: 'GET',
        headers: await this.authHeaders({
          'Content-Type': 'application/json',
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.data && Array.isArray(result.data.countries)) {
        return result.data.countries;
      }
      
      if (Array.isArray(result.countries)) {
        return result.countries;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching countries:', error);
      return [];
    }
  }

  // Get regions from geo_admin table
  async getRegions() {
    try {
      const response = await fetch(`${this.baseUrl}/getCountries`, {
        method: 'GET',
        headers: await this.authHeaders({
          'Content-Type': 'application/json',
        })
      });

      const result = await response.json();
      
      // Accept responses with or without 'success' property
      if (Array.isArray(result.regions)) {
        return result.regions;
      }
      if (result.data && Array.isArray(result.data.regions)) {
        return result.data.regions;
      }
      throw new Error('Failed to fetch regions: No regions array in response');
    } catch (error) {
      console.error('Error fetching regions:', error);
      return [];
    }
  }

  // Get countries by region
  async getCountriesByRegion(region) {
    try {
      const response = await fetch(`${this.baseUrl}/getCountries?region=${encodeURIComponent(region)}`, {
        method: 'GET',
        headers: await this.authHeaders({
          'Content-Type': 'application/json',
        })
      });

      const result = await response.json();
      
      // Accept responses with or without 'success' property
      if (Array.isArray(result.countries)) {
        return result.countries;
      }
      if (result.data && Array.isArray(result.data.countries)) {
        return result.data.countries;
      }
      throw new Error('Failed to fetch countries by region: No countries array in response');
    } catch (error) {
      console.error('Error fetching countries by region:', error);
      return [];
    }
  }

  // Get all sections for admin access
  getAllSections() {
    return this.getAvailableSections().map(section => section.value);
  }

  // Create a new user in the database
  async createUser({ email, name, lastName, country, accessLevel = 'country', allowedCountries = [], allowedSections = [], organization = '', title = '' }) {
    if (!email || !country) {
      throw new Error('Email and country are required');
    }

    try {
      const response = await fetch(`${this.baseUrl}/createUser`, {
        method: 'POST',
        headers: await this.authHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          email,
          name,
          lastName,
          country,
          accessLevel,
          allowedCountries: allowedCountries.length ? allowedCountries : [country],
          allowedSections: allowedSections.length ? allowedSections : this.getDefaultSections(accessLevel),
          organization,
          title
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async logLoginLog({ eventType, sessionId, pagePath = null, durationSeconds = null }) {
    if (!eventType || !sessionId) {
      throw new Error('eventType and sessionId are required');
    }

    try {
      const response = await fetch(`${this.baseUrl}/logLoginLog`, {
        method: 'POST',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          eventType,
          sessionId,
          pagePath,
          durationSeconds,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.error || result.message || `HTTP error! status: ${response.status}`);
      }

      return result.data || result;
    } catch (error) {
      console.error('Error logging login log:', error);
      throw error;
    }
  }

  async updateUserTitle(title) {
    const normalizedTitle = String(title || '').trim();
    if (!normalizedTitle) {
      throw new Error('Title is required');
    }

    try {
      const response = await fetch(`${this.baseUrl}/updateUserProfile`, {
        method: 'POST',
        headers: await this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ title: normalizedTitle })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.error || result.message || `HTTP error! status: ${response.status}`);
      }

      return result.data || result;
    } catch (error) {
      console.error('Error updating user title:', error);
      throw error;
    }
  }

  // Get default sections based on access level
  getDefaultSections(accessLevel) {
    switch (accessLevel) {
      case 'admin':
        return this.getAllSections();
      case 'global':
        return this.getAllSections().filter(section => section !== 'admin');
      case 'regional':
      case 'country':
        return ['stock-performance', 'co-instance', 'tracking', 'ticketing-system', 'scip'];
      default:
        return [];
    }
  }
}

const userService = new UserService();
export default userService; 